import { Ticket } from "lucide-react";
import { useTranslation } from "react-i18next";

import type { HomeCardLayout } from "@/campaigns";
import type { Raffle } from "@/campaigns/modules/raffle/types";
import { rafflePhase } from "@/campaigns/modules/raffle/types";
import { campaignKey } from "@/campaigns/campaign-key";
import { CompactCampaignCard } from "@/components/campaign-card/campaign-card-compact";
import { selectJoined } from "@/stores/campaigns.store";
import { useAppStore } from "@/stores";
import { formatPrizeTitle } from "@/utils/helpers";

/**
 * Home-feed card — a thin wrapper around the shared `CompactCampaignCard`. The hook is the top
 * prize (place 1, like the tournament's); the cue on the artwork leads with the player's ticket
 * count, or the state once the raffle is no longer earning. The card's own "Ends in" ticker
 * counts to the campaign end, which is when entries close.
 */
export const RaffleHomeCard = ({
  raffle,
  layout,
}: {
  raffle: Raffle;
  layout: HomeCardLayout;
}) => {
  const { t } = useTranslation();
  const joined = useAppStore((s) =>
    selectJoined(s, campaignKey(raffle.campaign))
  );
  const phase = rafflePhase(raffle);
  const top = raffle.prizes[0];

  const cue =
    phase === "awaiting-draw"
      ? t("raffle.entriesClosed", "Entries closed")
      : phase === "drawn"
        ? t("raffle.drawn", "Drawn")
        : joined && raffle.total_tickets !== null
          ? `${t("raffle.yourTickets", "Your tickets")}: ${raffle.total_tickets}`
          : undefined;

  return (
    <CompactCampaignCard
      campaign={raffle.campaign}
      typeLabel={t("campaign.typeRaffle", "Raffle")}
      typeIcon={<Ticket />}
      hookValue={top ? formatPrizeTitle(top.title, t) : null}
      hookLabel={t("campaign.topPrize", "Top prize")}
      hookIsCatalogItem={top?.is_catalog_item}
      metaExtra={cue}
      layout={layout}
    />
  );
};
